// ===================================================
// CONTEXTUAL NARRATIVES GENERATOR - VERSIÓN MEJORADA
// Genera narrativas con vocabulario infantil, concordancia
// gramatical correcta y contexto emocional para niños de 7 años
// ===================================================

/**
 * ========================================
 * SISTEMA DE CONCORDANCIA GRAMATICAL
 * ========================================
 */

/**
 * Obtiene la concordancia correcta para un objeto (género gramatical)
 */
function getGenderAgreement(obj) {
    // Objetos femeninos comunes
    const feminineWords = [
        'manzana', 'pera', 'naranja', 'fresa', 'sandía', 'ciruela', 'uva',
        'cesta', 'bolsa', 'caja', 'mesa', 'silla', 'galleta', 'tarta',
        'botella', 'lata', 'fruta', 'verdura', 'zanahoria', 'lechuga',
        'torre', 'venda', 'flor', 'piedra', 'mascota', 'entrada', 'concha'
    ];

    const objLower = obj.toLowerCase();
    return feminineWords.some(f => objLower.includes(f)) ? 'f' : 'm';
}

/**
 * Convierte plural a singular (heurística simple)
 */
function getSingular(word) {
    if (word.endsWith('es') && word.length > 3) {
        return word.slice(0, -2);
    }
    if (word.endsWith('s') && word.length > 2) {
        return word.slice(0, -1);
    }
    return word;
}

/**
 * Adapta la pregunta al género y número del objeto
 */
function generateQuestionWithAgreement(obj, quantity, isSubtraction = false, lang = 'es-ES') {
    const isEnglish = lang.startsWith('en');

    if (isEnglish) {
        const isPlural = quantity !== 1;
        const quantifier = 'how many'; // simplified for countable
        // A simple heuristic for English singular: usually just obj if passed as plural might be awkward without singular logic, 
        // but typically we pass the main noun. Assuming obj is plural.
        // Although simpler: just use plural in question "How many apples are there?" (even if 1).

        const verb = isSubtraction ? 'are left' : 'are there';
        return `How many ${obj} ${verb}?`; // Using obj (plural) works fine for general questions
    }

    const gender = getGenderAgreement(obj);
    const isPlural = quantity !== 1;

    let quantifier;
    if (gender === 'f') {
        quantifier = isPlural ? 'cuántas' : 'cuánta';
    } else {
        quantifier = isPlural ? 'cuántos' : 'cuánto';
    }

    // Ajustar el objeto al número
    const objForm = isPlural ? obj : getSingular(obj);

    // Seleccionar verbo apropiado
    const verb = isSubtraction ?
        (isPlural ? 'quedan' : 'queda') :
        (isPlural ? 'hay' : 'hay');

    return `¿${quantifier.charAt(0).toUpperCase() + quantifier.slice(1)} ${objForm} ${verb}?`;
}

/**
 * Devuelve la forma correcta del objeto según la cantidad (singular/plural)
 */
function formaCorrecta(cantidad, singular, plural) {
    return cantidad === 1 ? singular : plural;
}

/**
 * ========================================
 * PLANTILLAS MEJORADAS
 * ========================================
 */

// Names are already declared in narrativeAgent.js - reuse them
/**
 * Selecciona un nombre aleatorio
 */
function getRandomName() {
    // Check if BOY_NAMES and GIRL_NAMES exist from narrativeAgent.js
    if (typeof BOY_NAMES === 'undefined' || typeof GIRL_NAMES === 'undefined') {
        // Fallback names if narrativeAgent.js didn't load
        const fallbackBoys = ['Mateo', 'Hugo', 'Pablo', 'Lucas'];
        const fallbackGirls = ['Sofía', 'Lucía', 'María', 'Julia'];
        const allNames = [...fallbackBoys, ...fallbackGirls];
        return allNames[Math.floor(Math.random() * allNames.length)];
    }
    const allNames = [...BOY_NAMES, ...GIRL_NAMES];
    return allNames[Math.floor(Math.random() * allNames.length)];
}

/**
 * Plantillas mejoradas para SUMA con lenguaje infantil y contexto emocional
 */
const IMPROVED_SUM_TEMPLATES = [
    {
        template: (name1, num1, obj, num2) =>
            `${name1} va a la compra con su familia. Primero mete en la cesta ${num1} ${obj} que le gustan mucho. Después encuentra ${num2} ${obj} más que tienen muy buena pinta.`,
        usesNames: true,
        nameCount: 1
    },
    {
        template: (name1, num1, obj, num2, name2) =>
            `${name1} está haciendo la compra y coge ${num1} ${obj} con mucha alegría. Entonces aparece ${name2}, que es muy amable, y le da ${num2} ${obj} más para ayudarle.`,
        usesNames: true,
        nameCount: 2
    },
    {
        template: (name1, num1, obj, num2) =>
            `${name1} empieza a llenar su bolsa con ${num1} ${obj} muy frescos. Un poco después, su mamá le dice que puede coger ${num2} ${obj} más porque están en oferta.`,
        usesNames: true,
        nameCount: 1
    },
    {
        template: (name1, num1, obj, num2) =>
            `Hoy ${name1} va de compras y está muy contento. Al principio coge ${num1} ${obj} que se ven deliciosos. Luego descubre ${num2} ${obj} más que también quiere llevarse.`,
        usesNames: true,
        nameCount: 1
    },
    {
        template: (num1, obj, num2) =>
            `En la tienda, al principio del día, hay ${num1} ${obj} muy bonitos en una cesta. Cuando el camión de reparto trae más mercancía, el dependiente coloca ${num2} ${obj} más junto a los otros.`,
        usesNames: false,
        nameCount: 0
    },
    {
        template: (name1, num1, obj, num2, name2) =>
            `${name1} y ${name2} van juntos de compras y lo están pasando muy bien. ${name1} elige ${num1} ${obj} que huelen genial, y ${name2} añade ${num2} ${obj} más a la bolsa.`,
        usesNames: true,
        nameCount: 2
    },
    {
        template: (name1, num1, obj, num2) =>
            `A ${name1} le encanta ir de compras. Hoy ha elegido ${num1} ${obj} que se ven riquísimos. Cuando va a pagar, el dependiente le regala ${num2} ${obj} más porque es un cliente especial.`,
        usesNames: true,
        nameCount: 1
    }
];

const IMPROVED_SUM_TEMPLATES_EN = [
    {
        template: (name1, num1, obj, num2) =>
            `${name1} goes shopping with the family. First, puts ${num1} ${obj} in the basket. Then finds ${num2} more ${obj} that look great.`,
        usesNames: true,
        nameCount: 1
    },
    {
        template: (name1, num1, obj, num2, name2) =>
            `${name1} grabs ${num1} ${obj} happily. Then ${name2} appears and gives ${num2} more ${obj} to help.`,
        usesNames: true,
        nameCount: 2
    },
    {
        template: (name1, num1, obj, num2) =>
            `${name1} starts filling the bag with ${num1} fresh ${obj}. Later, mom says to take ${num2} more ${obj} because they are on sale.`,
        usesNames: true,
        nameCount: 1
    },
    {
        template: (name1, num1, obj, num2) =>
            `Today ${name1} goes shopping and is very happy. Starts by picking ${num1} delicious ${obj}. Then discovers ${num2} more ${obj} to take home.`,
        usesNames: true,
        nameCount: 1
    },
    {
        template: (num1, obj, num2) =>
            `At the store, early in the day, there are ${num1} nice ${obj} in a basket. When the delivery truck arrives, the clerk adds ${num2} more ${obj} next to the others.`,
        usesNames: false,
        nameCount: 0
    },
    {
        template: (name1, num1, obj, num2, name2) =>
            `${name1} and ${name2} go shopping together. ${name1} chooses ${num1} great ${obj}, and ${name2} adds ${num2} more ${obj} to the bag.`,
        usesNames: true,
        nameCount: 2
    },
    {
        template: (name1, num1, obj, num2) =>
            `${name1} loves shopping. Today picked ${num1} tasty ${obj}. At checkout, the clerk gives ${num2} more ${obj} as a gift.`,
        usesNames: true,
        nameCount: 1
    }
];

/**
 * Plantillas mejoradas para RESTA con lenguaje infantil y contexto emocional
 */
const IMPROVED_SUBTRACTION_TEMPLATES = [
    {
        template: (name1, num1, obj, num2) =>
            `${name1} tenía ${num1} ${obj} en su cesta, pero cuando llega a casa se da cuenta de que se han estropeado ${num2}. ¡Qué pena!`,
        usesNames: true,
        nameCount: 1
    },
    {
        template: (num1, obj, num2) =>
            `Al empezar el día, en la estantería había ${num1} ${obj} muy frescos. Durante la mañana, varios clientes han comprado ${num2}.`,
        usesNames: false,
        nameCount: 0
    },
    {
        template: (name1, num1, obj, num2) =>
            `${name1} cuenta con cuidado ${num1} ${obj} que quiere comprar. Pero luego piensa que son demasiados y devuelve ${num2} a la estantería.`,
        usesNames: true,
        nameCount: 1
    },
    {
        template: (num1, obj, num2) =>
            `Hay ${num1} ${obj} esperando en una caja para que los clientes los compren. A lo largo de la mañana, la gente va eligiendo y se llevan ${num2}.`,
        usesNames: false,
        nameCount: 0
    },
    {
        template: (name1, num1, obj, num2) =>
            `${name1} empieza con ${num1} ${obj} en su bolsa de la compra. Cuando va caminando hacia casa, sin querer se le caen ${num2} y no puede recuperarlos.`,
        usesNames: true,
        nameCount: 1
    },
    {
        template: (name1, num1, obj, num2) =>
            `${name1} ha comprado ${num1} ${obj} para toda la semana. Pero cuando llega a casa, descubre que ${num2} están un poco magullados y decide no usarlos.`,
        usesNames: true,
        nameCount: 1
    }
];

const IMPROVED_SUBTRACTION_TEMPLATES_EN = [
    {
        template: (name1, num1, obj, num2) =>
            `${name1} had ${num1} ${obj} in the basket, but upon arriving home, realized that ${num2} were spoiled. What a pity!`,
        usesNames: true,
        nameCount: 1
    },
    {
        template: (num1, obj, num2) =>
            `At the start of the day, there were ${num1} fresh ${obj} on the shelf. During the morning, customers bought ${num2}.`,
        usesNames: false,
        nameCount: 0
    },
    {
        template: (name1, num1, obj, num2) =>
            `${name1} carefully counts ${num1} ${obj} to buy. But then thinks it's too many and returns ${num2} to the shelf.`,
        usesNames: true,
        nameCount: 1
    },
    {
        template: (num1, obj, num2) =>
            `There are ${num1} ${obj} waiting in a box. Throughout the morning, people choose and take away ${num2}.`,
        usesNames: false,
        nameCount: 0
    },
    {
        template: (name1, num1, obj, num2) =>
            `${name1} starts with ${num1} ${obj} in the shopping bag. While walking home, ${num2} accidentally fall out and cannot be recovered.`,
        usesNames: true,
        nameCount: 1
    },
    {
        template: (name1, num1, obj, num2) =>
            `${name1} bought ${num1} ${obj} for the week. But at home, discovers that ${num2} are a bit bruised and decides not to use them.`,
        usesNames: true,
        nameCount: 1
    }
];

/**
 * ========================================
 * FUNCIÓN PRINCIPAL DE GENERACIÓN
 * ========================================
 */

/**
 * Genera narrativas contextuales mejoradas
 * @param {Object} mathData - {num1, num2, distractor, answer}
 * @param {string} logicType - 'suma', 'resta', 'comparacion'
 * @param {string} contextId - ID del contexto
 * @param {string} level - 'beginner', 'advanced', 'expert'
 * @param {Object} [contextObjectOverride] - Objeto opcional con {singular, plural, emoji}
 * @returns {Object} {story_text, question}
 */
function generateContextualNarrative(mathData, logicType, contextId, level, contextObjectOverride = null, lang = 'es-ES') {
    const { num1, num2, distractor, answer } = mathData;
    const useDistractor = distractor !== null && distractor !== undefined;
    const isEnglish = lang.startsWith('en');
    const langKey = isEnglish ? 'en' : 'es';

    // Obtener objeto del contexto: Usar override si existe, sino intentar buscar globalmente, sino fallback
    let contextObj = contextObjectOverride;

    if (!contextObj) {
        // Intentar buscar en global si existe
        if (typeof CONTEXT_OBJECTS !== 'undefined' && CONTEXT_OBJECTS[contextId]) {
            // Si es array, coger uno al azar (simple, sin balanceo aquí)
            const objects = CONTEXT_OBJECTS[contextId];
            if (Array.isArray(objects)) {
                contextObj = objects[Math.floor(Math.random() * objects.length)];
            } else {
                contextObj = objects;
            }
        }
    }

    // Fallback final
    if (!contextObj) {
        contextObj = {
            plural: { es: 'objetos', en: 'objects' },
            singular: { es: 'objeto', en: 'object' }
        };
    }

    const objPlural = contextObj.plural[langKey] || contextObj.plural.es || 'objetos';
    const objSingular = contextObj.singular[langKey] || contextObj.singular.es || getSingular(objPlural);

    // Generar según tipo lógico
    switch (logicType) {
        case 'suma':
            return generateSumNarrative(num1, num2, distractor, objPlural, objSingular, useDistractor, answer, lang);
        case 'resta':
            return generateSubtractionNarrative(num1, num2, distractor, objPlural, objSingular, useDistractor, answer, lang);
        default:
            return generateFallbackNarrative(mathData, logicType, objPlural, lang);
    }
}

/**
 * Genera narrativa de SUMA usando plantillas mejoradas
 */
function generateSumNarrative(num1, num2, distractor, objPlural, objSingular, useDistractor, answer, lang = 'es-ES') {
    const isEnglish = lang.startsWith('en');

    // Seleccionar nombres aleatorios
    const name1 = getRandomName();
    let name2 = getRandomName();
    while (name1 === name2) name2 = getRandomName(); // Evitar duplicados

    // Seleccionar una plantilla aleatoria
    const templates = isEnglish ? IMPROVED_SUM_TEMPLATES_EN : IMPROVED_SUM_TEMPLATES;
    const selectedTemplate = templates[Math.floor(Math.random() * templates.length)];

    // Ejecutar plantilla
    let storyText = '';
    if (selectedTemplate.nameCount === 2) {
        storyText = selectedTemplate.template(name1, num1, objPlural, num2, name2);
    } else if (selectedTemplate.nameCount === 1) {
        storyText = selectedTemplate.template(name1, num1, objPlural, num2);
    } else {
        storyText = selectedTemplate.template(num1, objPlural, num2);
    }

    // Añadir distractor si corresponde
    if (useDistractor) {
        if (isEnglish) {
            storyText += ` Also, ${name2} has ${distractor} ${objPlural} saved that do not count for this problem.`;
        } else {
            storyText += ` Por otro lado, ${name2} tiene ${distractor} ${objPlural} guardados que no cuentan para este problema.`;
        }
    }

    // Generar pregunta con concordancia correcta
    const question = generateQuestionWithAgreement(objPlural, answer, false, lang);

    return {
        story_text: storyText + ' ' + question,
        question: question
    };
}

/**
 * Genera narrativa de RESTA usando plantillas mejoradas
 */
function generateSubtractionNarrative(num1, num2, distractor, objPlural, objSingular, useDistractor, answer, lang = 'es-ES') {
    const isEnglish = lang.startsWith('en');

    // Seleccionar nombres aleatorios
    const name1 = getRandomName();
    let name2 = getRandomName();
    while (name1 === name2) name2 = getRandomName(); // Evitar duplicados

    // Seleccionar una plantilla aleatoria
    const templates = isEnglish ? IMPROVED_SUBTRACTION_TEMPLATES_EN : IMPROVED_SUBTRACTION_TEMPLATES;
    const selectedTemplate = templates[Math.floor(Math.random() * templates.length)];

    // Ejecutar plantilla
    let storyText = '';
    if (selectedTemplate.nameCount === 2) {
        storyText = selectedTemplate.template(name1, num1, objPlural, num2, name2);
    } else if (selectedTemplate.nameCount === 1) {
        storyText = selectedTemplate.template(name1, num1, objPlural, num2);
    } else {
        storyText = selectedTemplate.template(num1, objPlural, num2);
    }

    // Añadir distractor si corresponde
    if (useDistractor) {
        if (isEnglish) {
            storyText += ` Also, ${name2} has ${distractor} ${objPlural} saved that do not count for this problem.`;
        } else {
            storyText += ` Por otro lado, ${name2} tiene ${distractor} ${objPlural} guardados que no cuentan para este problema.`;
        }
    }

    // Generar pregunta con concordancia correcta
    const question = generateQuestionWithAgreement(objPlural, answer, true, lang);

    return {
        story_text: storyText + ' ' + question,
        question: question
    };
}

/**
 * Narrativa de fallback simple
 */
function generateFallbackNarrative(mathData, logicType, objPlural, lang = 'es-ES') {
    const { num1, num2, answer } = mathData;
    const isEnglish = lang.startsWith('en');
    const objSingular = getSingular(objPlural);

    const obj1Form = formaCorrecta(num1, objSingular, objPlural);
    const obj2Form = formaCorrecta(num2, objSingular, objPlural);

    const stories = {
        'suma': {
            story_text: isEnglish
                ? `There are ${num1} ${objPlural} and then ${num2} more ${objPlural} appear. ${generateQuestionWithAgreement(objPlural, answer, false, lang)}`
                : `Hay ${num1} ${obj1Form} y luego aparecen ${num2} ${obj2Form} más. ${generateQuestionWithAgreement(objPlural, answer, false, lang)}`,
            question: generateQuestionWithAgreement(objPlural, answer, false, lang)
        },
        'resta': {
            story_text: isEnglish
                ? `There are ${num1} ${objPlural} and ${num2} are used. ${generateQuestionWithAgreement(objPlural, answer, true, lang)}`
                : `Hay ${num1} ${obj1Form} y se usan ${num2}. ${generateQuestionWithAgreement(objPlural, answer, true, lang)}`,
            question: generateQuestionWithAgreement(objPlural, answer, true, lang)
        }
    };

    return stories[logicType] || stories['suma'];
}

// Exportar funciones al window para compatibilidad
if (typeof window !== 'undefined') {
    window.generateContextualNarrative = generateContextualNarrative;
    window.getSingular = getSingular;
    window.getGenderAgreement = getGenderAgreement;
    window.generateQuestionWithAgreement = generateQuestionWithAgreement;
    window.formaCorrecta = formaCorrecta;
}

console.log('✅ [ContextualNarratives] Sistema mejorado de narrativas cargado');
